import { Space } from 'antd';
import { DeleteOutlined, FileAddOutlined } from '@ant-design/icons';
import Table, { ColumnsType } from 'antd/lib/table';
import { isEmpty } from 'lodash';
import { handleNullValues } from 'components/ui/base/table/TableUtils';
import { FC, useCallback, useMemo } from 'react';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { DeclarationName } from 'store/declarations/enums/common/declaration-name';
import { IrelandExportItem } from 'store/declarations/ireland/export-declaration';
import { GoodsShipmentItem as H7GoodsShipmentItem } from 'store/declarations/ireland/h7-import-declaration';
import { GoodsShipmentItem as H1GoodsShipmentItem } from 'store/declarations/ireland/import-declaration';
import { ListPayload } from '../../../../core/http/response';
import useCountries from '../../../../hooks/useCountries';
import useFormUtils from '../../../../hooks/useFormUtils';
import { TableChangeParams } from '../../../../utils/tableHelpers';
import getExportProductsTableColumns from './columns/ExportProductsTableColumns';
import getH1ProductsTableColumns from './columns/H1ProductsTableColumns';
import getH7ProductsTableColumns from './columns/H7ProductsTableColumns';
import getNCTSProductsTableColumns from './columns/NCTSProductsTableColumns';
import { ProductsErrorCounts } from 'views/declarations/declaration-tabs/declaration-form/tabs/products/getErrorCountsByRow';
import ProductErrorCount from 'views/declarations/common/ProductErrorCount';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import useDeclarations from 'hooks/useDeclarations';
import getProductsTablePagination from 'views/declarations/common/getProductsTablePagination';
import getTableRowSelection from 'views/declarations/common/getTableRowSelection';

interface Props {
    data:
        | ListPayload<H1GoodsShipmentItem>
        | ListPayload<H7GoodsShipmentItem>
        | ListPayload<IrelandExportItem>
        | undefined
        | null;
    onDelete?: (id: string) => void;
    onEdit?: (id: string) => void;
    onAddToTemplate?: (item: string) => void;
    loading?: boolean;
    viewOnly?: boolean;
    errorCounts?: ProductsErrorCounts;
    selectedRowKeys?: React.Key[];
    onChange: (params: TableChangeParams) => void;
    onSelectedRowKeysChange?: (keys: React.Key[]) => void;
}

const IrelandProductsTable: FC<Props> = ({
    data,
    onDelete,
    onEdit,
    onAddToTemplate,
    loading,
    viewOnly,
    errorCounts,
    selectedRowKeys,
    onChange,
    onSelectedRowKeysChange,
    ...rest
}) => {
    const { formType, internalType } = useFormUtils();
    const { getNameFromCountryCode } = useCountries();
    const { declaration } = useDeclarations();

    const handleTableValues = useCallback((_index: number, value?: string | number) => handleNullValues(value), []);

    const dataColumns: ColumnsType<any> = useMemo(() => {
        if (internalType === DeclarationInternalType.NCTS) return getNCTSProductsTableColumns(handleTableValues);
        if (internalType === DeclarationInternalType.EXPORT)
            return getExportProductsTableColumns(getNameFromCountryCode);
        if (formType === DeclarationName.H7) return getH7ProductsTableColumns();
        return getH1ProductsTableColumns(
            declaration?.irelandImportDeclaration?.invoiceCurrency,
            getNameFromCountryCode,
            declaration?.status
        );
    }, [
        internalType,
        handleTableValues,
        getNameFromCountryCode,
        formType,
        declaration?.irelandImportDeclaration?.invoiceCurrency,
        declaration?.status,
    ]);

    const actionsColumn: ColumnsType<any> = [
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            width: 1,
            render: (text, record: H1GoodsShipmentItem | H7GoodsShipmentItem | IrelandExportItem) => (
                <Space size={8}>
                    {viewOnly ? (
                        formType !== 'ARRIVAL' &&
                        onAddToTemplate && (
                            <IconButton
                                icon={<FileAddOutlined />}
                                tooltipText="Add to templates"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddToTemplate(record?.id!);
                                }}
                            />
                        )
                    ) : (
                        <>
                            {formType !== 'ARRIVAL' && onAddToTemplate && (
                                <IconButton
                                    icon={<FileAddOutlined />}
                                    tooltipText="Add to templates"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddToTemplate(record?.id!);
                                    }}
                                />
                            )}
                            <IconButton
                                icon={<DeleteOutlined />}
                                tooltipText="Delete"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete?.(record?.id!);
                                }}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const notificationsColumn: ColumnsType<any> = [
        {
            dataIndex: 'icon',
            key: 'icon',
            width: 1,
            render: (text, record, index: number) => {
                return errorCounts?.[index] ? <ProductErrorCount count={errorCounts[index]} /> : null;
            },
        },
    ];

    const allColumns = useMemo(() => {
        const hasActions = onAddToTemplate && onDelete;
        const hasErrors = !isEmpty(errorCounts);
        const iconCols = hasErrors ? notificationsColumn : [];

        if (hasActions) {
            return [...iconCols, ...dataColumns, ...actionsColumn];
        }

        return [...dataColumns, ...iconCols];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onAddToTemplate, errorCounts]);

    return (
        <StyledTableWrapper>
            <Table
                rowKey="id"
                dataSource={data?.list}
                columns={allColumns}
                loading={loading}
                rowSelection={getTableRowSelection<H1GoodsShipmentItem | H7GoodsShipmentItem | IrelandExportItem>({
                    viewOnly,
                    selectedRowKeys,
                    onSelectedRowKeysChange,
                })}
                rowClassName="row-align-top"
                onRow={(record, rowIndex) => {
                    return {
                        onClick: (event) => {
                            event.stopPropagation();
                            return onEdit && onEdit(record.id!);
                        },
                    };
                }}
                onChange={(pagination, _, sorter) => onChange({ pagination, sorter })}
                pagination={getProductsTablePagination(data)}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};
export default IrelandProductsTable;
